import { computed, ElementRef, Injectable, Signal, signal } from '@angular/core';
import { Widget } from '../models/dashboard';
import { WidgetFactory } from '../factory/widget.factory';
import { CdkDragEnd, CdkDragMove } from '@angular/cdk/drag-drop';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  // Signal, das die aktuell hinzugefügten Widgets hält
  private _addedWidgets = signal<Widget[]>([]);
  // Zähler für automatisch generierte Widget-IDs
  private _widgetIdCounter = 1;
  // Anzahl der Spalten im Grid (kann bei Bedarf angepasst werden)
  private columnsCount = 4;

  // Signal für alle verfügbaren Widgets (z.B. für ein Auswahlmenü)
  widgets = signal<Widget[]>([]);
  // Exponierte Signal-Property, um auf die hinzugefügten Widgets zuzugreifen
  addedWidgets = this._addedWidgets;

  /**
   * Aktualisiert ein existierendes Widget, indem nur die übergebenen Felder übernommen werden.
   * @param id - ID des Widgets, das aktualisiert werden soll
   * @param widget - Teilweise Felder, die überschrieben werden sollen
   */
  updateWidget(id: number, widget: Partial<Widget>): void {
    // Finde den Index des zu ändernden Widgets im aktuellen Array
    const index = this.addedWidgets().findIndex(w => w.widgetId === id);
    if (index !== -1) {
      // Kopiere das Array und ersetze das geänderte Widget durch ein neues Objekt
      const newWidgets = [...this.addedWidgets()];
      newWidgets[index] = { ...newWidgets[index], ...widget };
      this.addedWidgets.set(newWidgets);
    }
  }

  /**
   * Fügt ein neues Widget mit Standardwerten hinzu.
   * @param widget - Partial mit initialen Werten (z.B. Label, columnSpan, ...)
   */
  addWidget(widget: Partial<Widget>) {
    // Generiere eine eindeutige ID für das neue Widget
    const id = this._widgetIdCounter++;
    // Erzeuge das vollständige Widget-Objekt mit Standardwerten, falls nicht gesetzt
    const newWidget: Widget = {
      id,
      label: widget.label || `Widget ${id}`,
      columnPosition: widget.columnPosition ?? 1,
      rowPosition: widget.rowPosition ?? 1,
      columnSpan: widget.columnSpan ?? 1,
      rowSpan: widget.rowSpan ?? 1,
      content: widget.content || WidgetFactory.createTrendWidget().content,
      widgetId: widget.widgetId || 0,
      backgroundColor: widget.backgroundColor || '#ffffff',
      color: widget.color || '#000000',
      contentData: widget.contentData || '{}'
    };

    // Suche die erste freie Position, damit es nicht überlappt
    let rowColSpan = this.findFirstFreePosition(newWidget);
    newWidget.columnPosition = rowColSpan.columnPosition;
    newWidget.rowPosition = rowColSpan.rowPosition;

    // Füge das neue Widget zum Signal-Array hinzu
    this._addedWidgets.update(widgets => [...widgets, newWidget]);
  }

  /**
   * (Veraltet/Optional) Aktualisiert die Position eines Widgets per direkter x/y-Koordinaten.
   * Hier wird aber eher columnPosition/rowPosition verwendet, daher selten genutzt.
   * @param id - ID des Widgets
   * @param x  - neue x-Koordinate (z.B. Spaltenindex)
   * @param y  - neue y-Koordinate (z.B. Zeilenindex)
   */
  updateWidgetPosition(id: number, x: number, y: number) {
    this._addedWidgets.update(widgets =>
      widgets.map(w =>
        w.widgetId === id ? { ...w, x, y } : w
      )
    );
  }

  /**
   * Entfernt ein Widget anhand seiner ID.
   * @param id - ID des Widgets, das gelöscht werden soll
   */
  removeWidget(id: number) {
    this._addedWidgets.update(widgets =>
      widgets.filter(w => w.widgetId !== id)
    );
  }

  /**
   * Setzt alle hinzugefügten Widgets zurück (leert das Dashboard).
   */
  reset() {
    this._addedWidgets.set([]);
  }

  /**
   * Berechnet beim Drag-and-Drop-Event die neue columnPosition/rowPosition
   * anhand der tatsächlichen Position des DOM-Elements im Grid.
   * Wird meist in onDragEnded oder onDragMoved verwendet.
   *
   * @param event              - Das CdkDragEnd- oder CdkDragMove-Event
   * @param widget             - Das betroffene Widget-Objekt
   * @param gridElementRef     - Signal, das auf das Dashboard-Element zeigt
   * @param gridGap            - Abstand (gap) zwischen den Grid-Zellen in Pixel
   * @param columnsCount       - Anzahl der Spalten im Grid
   * @param rowHeight          - Höhe einer Zeile inkl. gap in Pixel
   * @returns { newCol, newRow } - Neue 1-basierte Spalten- und Zeilenposition
   */
  updateWidgetPositionByDrag(
    event: any,
    widgetId: number,
    gridElement: HTMLElement,
    colCount = 4,
    rowHeight = 166
  ) {
    // Grid- und Widget-Rects abfragen
    const gridRect = gridElement.getBoundingClientRect();
    const widgetRect = event.source.el.nativeElement.getBoundingClientRect();

    // Pixel-Offset der oberen linken Ecke des Widgets relativ zum Grid
    const relativeLeft = widgetRect.left - gridRect.left;
    const relativeTop = widgetRect.top - gridRect.top;

    // Berechne die Breite einer Spalte
    const gridWidth = gridElement.offsetWidth;
    const colWidth = gridWidth / colCount;

    // Ermittle 0-basierte Indizes per floor
    const x = Math.floor(relativeLeft / colWidth);
    const y = Math.floor(relativeTop / rowHeight);

    // Setze die neuen Koordinaten
    this.updateWidgetPosition(widgetId, x, y);
  }

  /**
   * Computed-Signal für alle Widgets, die noch nicht hinzugefügt sind.
   * Nutzt die vorhandenen Widgets (this.widgets) und filtert jene heraus, die
   * bereits in addedWidgets enthalten sind. Gibt ihnen ein "command", das
   * auf addWidget verweist, sodass sie per Klick eingefügt werden können.
   */
  widgetsToAdd = computed(() => {
    // IDs aller bereits hinzugefügten Widgets
    const addedIds = this.addedWidgets().map(w => w.widgetId);

    // Filtere verfügbare Widgets aus dem Basis-Array heraus
    const availableWidgets = this.widgets()
      .filter(w => !addedIds.includes(w.widgetId))
      .map(widget => ({
        label: widget.label,         // Anzeige-Label im Menü
        widget,                      // Widget-Objekt (Partial-Daten)
        command: () => this.addWidget(widget) // Klick-Handler zum Hinzufügen
      }));

    // Wenn keine Widgets mehr verfügbar sind, Rückgabe eines Platzhalters
    return availableWidgets.length > 0
      ? availableWidgets
      : [{ label: 'Kein Widget vorhanden' }];
  });

  constructor() {
    // Initialisiere die Liste aller verfügbaren Widgets über die Factory
    this.widgets.set(WidgetFactory.createWidgetList());
    // Leere das Dashboard-Array beim Start
    this._addedWidgets.set([]);
  }

  /**
   * Berechnet aus einem CdkDragEnd- oder CdkDragMove-Event die
   * gerasterte Grid-Position (Spalte/Zeile) basierend auf gap und Spaltengröße.
   *
   * @param event                - Drag-Event (Move oder End)
   * @param widget               - Das Widget, das verschoben wird
   * @param dashboardElementRef  - Signal, das auf das Dashboard-DOM-Element verweist
   * @param gridGap              - Pixel-Abstand (gap) zwischen den Zellen
   * @param columnsCount         - Anzahl der Spalten im Grid
   * @param rowHeight            - Höhe einer Zeile inkl. gap in Pixel
   * @returns { newCol, newRow } - Neue 1-basierte Grid-Koordinaten
   */
  public calculateGridPosition(
    event: CdkDragEnd | CdkDragMove,
    widget: Widget,
    dashboardElementRef: Signal<ElementRef<any>>,
    gridGap: number,
    columnsCount: number,
    rowHeight: number
  ): { newCol: number; newRow: number } {
    // Abfrage der Bounding-Redecks für Grid und Widget
    const gridRect =
      dashboardElementRef().nativeElement.getBoundingClientRect();
    const widgetRect =
      event.source.element.nativeElement.getBoundingClientRect();

    // 1) Pixel-Offset der linken oberen Ecke relativ zum Grid
    const xPos = widgetRect.left - gridRect.left;
    const yPos = widgetRect.top - gridRect.top;

    // 2) Berechne die reine Breite einer Spalte nach Abzug aller Gaps
    const totalGapWidth = gridGap * (columnsCount - 1);
    const colWidth = (gridRect.width - totalGapWidth) / columnsCount;

    // 3) Errechne 0-basierte Indizes, gerundet für Snap-Verhalten
    const colIndex = Math.round(xPos / (colWidth + gridGap));
    const rowIndex = Math.round(yPos / (rowHeight + gridGap));

    // 4) In 1-basierte CSS-Grid-Linien umwandeln und Eingabewerte validieren
    const maxColStart = columnsCount - widget.columnSpan + 1;
    const newCol = Math.min(maxColStart, Math.max(1, colIndex + 1));
    const newRow = Math.max(1, rowIndex + 1);

    return { newCol, newRow };
  }

  /**
   * Prüft, ob zwei Widgets sich im Grid überschneiden (Kollision).
   * Liefert true, wenn eine Überschneidung vorliegt, false sonst.
   *
   * @param a - Erstes Widget
   * @param b - Zweites Widget
   */
  private isOverlap(a: Widget, b: Widget): boolean {
    return !(
      // a komplett links von b
      a.columnPosition + a.columnSpan <= b.columnPosition ||
      // a komplett rechts von b
      b.columnPosition + b.columnSpan <= a.columnPosition ||
      // a komplett über b
      a.rowPosition + a.rowSpan <= b.rowPosition ||
      // a komplett unter b
      b.rowPosition + b.rowSpan <= a.rowPosition
    );
  }

  /**
   * Schiebt rekursiv alle Widgets, die mit 'moved' kollidieren, nach unten,
   * sodass keine Überlappungen mehr bestehen.
   *
   * @param moved - Das Widget, das verschoben wurde und nun Prüfpointer ist
   */
  public resolveCollisions(moved: Widget): void {
    this
      .addedWidgets()
      // Filtere alle Widgets heraus, die nicht 'moved' sind und kollidieren
      .filter((w) => w.id !== moved.id && this.isOverlap(moved, w))
      .forEach((collided) => {
        // Verschiebe das kollidierte Widget direkt unter moved
        collided.rowPosition = moved.rowPosition + moved.rowSpan;
        // Prüfe, ob durch diese Verschiebung neue Kollisionen entstanden sind
        this.resolveCollisions(collided);
      });
  }

  /**
   * Sucht die erste freie Position (Spalte, Zeile) für ein neues Widget,
   * damit es nicht mit existierenden Widgets überlappt.
   *
   * @param newWidget   - Das neue Widget mit gewünschter Breite/Höhe (columnSpan/rowSpan)
   * @param maxRowCap   - Maximale Zeilenanzahl, die durchsucht wird (Standard 100)
   * @returns Objekt mit columnPosition und rowPosition (beide 1-basiert)
   */
  findFirstFreePosition(
    newWidget: Widget,
    maxRowCap = 100  // Prüft bis maximal 100 Zeilen, kann nach Bedarf angepasst werden
  ): { columnPosition: number; rowPosition: number } {
    // Durchlaufe alle Zeilen von 1 bis maxRowCap
    for (let row = 1; row <= maxRowCap; row++) {
      // Durchlaufe alle möglichen Startspalten (sodass newWidget innerhalb des Rasters bleibt)
      for (let col = 1; col <= this.columnsCount - newWidget.columnSpan + 1; col++) {
        // Erzeuge ein temporäres "Test-Widget" an dieser Position
        const testWidget: Widget = {
          widgetId: -1,             // Dummy-ID, da nur Position geprüft wird
          id: -1,                   // Dummy-ID
          label: 'Test',            // Label nur als Platzhalter
          content: newWidget.content,
          backgroundColor: newWidget.backgroundColor,
          color: newWidget.color,
          contentData: newWidget.contentData,

          columnPosition: col,
          rowPosition: row,
          columnSpan: newWidget.columnSpan,
          rowSpan: newWidget.rowSpan,
        };
        // Prüfen, ob das Test-Widget mit irgendeinem existierenden kollidiert
        const collision = this.addedWidgets().some(w => this.isOverlap(testWidget, w));
        if (!collision) {
          // Gefundene freie Zelle: Rückgabe der Koordinaten
          return { columnPosition: col, rowPosition: row };
        }
      }
    }
    // Wenn bis maxRowCap keine freie Stelle gefunden wurde, setze es in Zeile maxRowCap+1, Spalte 1
    return { columnPosition: 1, rowPosition: maxRowCap + 1 };
  }
}
